import { useState, useEffect, useCallback } from 'react';
import { projectService } from '../../../services/project.service';
import type { Project } from '../../../services/project.service';
import posthog from '../../../posthog';

export function useProjects(limit = 12) {
  const [searchTerm, setSearchTerm] = useState('');
  const [projectsList, setProjectsList] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState('last_modified');
  const [sortOrder, setSortOrder] = useState(-1);

  // Drawer Edit Form States
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [summary, setSummary] = useState('');
  const [objective, setObjective] = useState('');
  const [remarks, setRemarks] = useState('');

  // JSON Quick-Fill States
  const [jsonText, setJsonText] = useState('');
  const [jsonStatus, setJsonStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');

  // Saving States
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // True only when this form was populated by an AI analysis in the same drawer session
  const [filledByAI, setFilledByAI] = useState(false);

  // The extracted document text from the most recent AI analysis, kept in
  // memory so the reflection call can use the original document if the user
  // corrects any field before saving.
  const [documentText, setDocumentText] = useState<string | null>(null);

  // The fields proposed by the AI during this session, used to detect corrections.
  const [sessionAiProposed, setSessionAiProposed] = useState<any>(null);

  // Fetch Projects List
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await projectService.getProjectsList({
        page,
        limit,
        q: searchTerm.trim() || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setProjectsList(data.projects || []);
      setTotalPages(data.pages || 1);
      setTotalItems(data.total || 0);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching projects.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchProjects();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [fetchProjects]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 1 ? -1 : 1));
    } else {
      setSortBy(field);
      setSortOrder(-1);
    }
    setPage(1);
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setCategory(project.category || '');
    setSubCategory(project.sub_category || '');
    setSummary(project.summary || '');
    setObjective(project.objective || '');
    setRemarks(project.remarks || '');
    setJsonText('');
    setJsonStatus('idle');
    setSaveSuccess(false);
    setSaveError(null);
    setFilledByAI(false);
    setSessionAiProposed(null);
  };

  const handleSaveChanges = async () => {
    if (!selectedProject) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      const updated = await projectService.updateProject(selectedProject._id, {
        category: category || null,
        sub_category: subCategory || null,
        summary: summary || null,
        objective: objective || null,
        remarks: remarks || null,
        ai_session: filledByAI,
        document_text: documentText || null,
      });

      // Update local state list
      setProjectsList((prev) =>
        prev.map((p) => (p._id === updated._id ? updated : p))
      );
      setSelectedProject(updated);
      posthog.capture('campaign_configuration_saved');
      setSaveSuccess(true);

      setTimeout(() => {
        setSelectedProject(null);
      }, 1000);
    } catch (err: any) {
      setSaveError(err.message || 'An error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  return {
    searchTerm,
    handleSearchChange,
    projectsList,
    loading,
    error,
    page,
    setPage,
    totalPages,
    totalItems,
    sortBy,
    sortOrder,
    handleSort,

    // Selection/Drawer States
    selectedProject,
    setSelectedProject,
    handleSelectProject,
    category,
    setCategory,
    subCategory,
    setSubCategory,
    summary,
    setSummary,
    objective,
    setObjective,
    remarks,
    setRemarks,

    // JSON Import States
    jsonText,
    setJsonText,
    jsonStatus,
    setJsonStatus,

    // AI Session Tracking
    filledByAI,
    setFilledByAI,
    documentText,
    setDocumentText,
    sessionAiProposed,
    setSessionAiProposed,

    // Saving Actions
    saving,
    saveSuccess,
    saveError,
    handleSaveChanges,
    fetchProjects,
  };
}
